import { useMemo } from 'react';
import { createPortal } from 'react-dom';
import { Divider, Flex, Typography } from 'antd';

import { useProject } from '@contexts/Project.context';
import { formatNumber } from '@utils/helpers';
import SidebarTemplate from '../../../sidebars/sidebarElements/SidebarTemplate';
import { COLLECTION_SIDEBAR_IDS } from '../constants';
import { collectionDspSummary } from '../utils/collectionDspSummary';
import HexagonStats from './HexagonStats';

const { Text } = Typography;

/**
 * The "Digital Sample Plots" details panel, rendered into the modify-layers
 * portal (same pattern as CollectionHexagonPanel).
 *
 * Shows the whole-collection aggregate, reusing `HexagonStats` — the summary
 * exposes the same `{ stats, detections }` pair a single plot does, so one
 * presentational component covers both.
 *
 * Unlike CollectionHexagonPanel it has no selected-unit mode. There, clicking a
 * cell selects it and this panel shows that cell's detail. Here a plot *is* a
 * drone image, so a click opens the image instead (see CollectionDspLayer), and
 * per-plot detail is read from the hover tooltip. That split matches the
 * model DSP flow, where the footprint tooltip carries per-plot numbers and the
 * side panel carries the aggregate.
 *
 * @param {object} props
 * @param {import('../utils/collectionDspToPlots').DspPlot[]} props.plots
 */
const CollectionDspPanel = ({ plots }) => {
	const { modifyLayersSidebarRef } = useProject();

	const summary = useMemo(() => collectionDspSummary(plots), [plots]);

	if (!modifyLayersSidebarRef?.current) return null;

	return createPortal(
		<SidebarTemplate
			zindex="2"
			sidebarId={COLLECTION_SIDEBAR_IDS.dspPanel}
			header="Digital Sample Plots"
			closeable>
			<Flex vertical gap={12} style={{ padding: '12px 16px' }}>
				<Flex align="center" justify="space-between" gap={8}>
					<Text style={{ fontSize: '0.85rem' }}>Sample plots</Text>
					<Text style={{ fontSize: '0.85rem' }}>
						{formatNumber(summary?.plotCount ?? 0)}
					</Text>
				</Flex>

				<Divider style={{ margin: 0 }} />

				{summary ? (
					<>
						<HexagonStats stats={summary.stats} detections={summary.detections} />
						{/* Both totals above need a caveat, and this is where they are
						    first read. Footprints overlap, so their sum exceeds the
						    surveyed area (/detected-area reports the union), and the
						    endpoint keeps a tree seen by two overlapping images as an
						    observation in both plots. */}
						<Text type="secondary" style={{ fontSize: '0.7rem' }}>
							Totals are per-plot sums: overlapping ground and objects seen by more
							than one image are counted once per image, so both exceed the survey
							figures. Hover a plot on the map for its own numbers.
						</Text>
					</>
				) : (
					<Text type="secondary" style={{ fontSize: '0.8rem' }}>
						No sample plot data.
					</Text>
				)}
			</Flex>
		</SidebarTemplate>,
		modifyLayersSidebarRef.current
	);
};

export default CollectionDspPanel;
